import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Save, Workflow, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { TAGS } from "../lib/stages";
import { useStages } from "../lib/useStages";

function defaultForm(firstStage) {
  return { to_stage: firstStage || "New", template_id: "", tag: "", is_active: true, description: "" };
}

export default function AdminStageEmailRules() {
  const { stages: STAGES } = useStages();
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | rule id
  const [form, setForm] = useState(defaultForm(STAGES[0]));

  const load = async () => {
    try {
      const [r, t] = await Promise.all([
        api.get("/stage-email-rules"),
        api.get("/email-templates"),
      ]);
      setRules(r.data);
      setTemplates(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...defaultForm(STAGES[0]), template_id: templates[0]?.id || "" });
    setEditing("new");
  };

  const openEdit = (r) => {
    setForm({
      to_stage: r.to_stage,
      template_id: r.template_id,
      tag: r.tag || "",
      is_active: r.is_active !== false,
      description: r.description || "",
    });
    setEditing(r.id);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.template_id) {
      toast.error("Please pick a template");
      return;
    }
    const payload = { ...form, tag: form.tag || null };
    try {
      if (editing === "new") {
        await api.post("/stage-email-rules", payload);
        toast.success("Rule created");
      } else {
        await api.patch(`/stage-email-rules/${editing}`, payload);
        toast.success("Rule updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule: when stage → "${r.to_stage}", send "${r.template_name}"?`)) return;
    try {
      await api.delete(`/stage-email-rules/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Layout title="Stage Email Triggers" breadcrumb={["Admin", "Stage Email Triggers"]}>
      <div className="space-y-5">
        <div className="bg-orange-50/40 border border-orange-200 rounded-xl p-4 flex items-start gap-3" data-testid="stage-rules-help">
          <Workflow size={18} className="text-orange-600 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-700">
            <div className="font-semibold text-slate-900 mb-0.5">How stage triggers work</div>
            <p className="text-xs leading-relaxed">
              When a lead moves <b>into</b> a stage (e.g. "Not-Connected"), the LMS auto-sends the linked template
              <b> from the lead owner's connected Gmail</b> to the lead's POC email. Optionally restrict to a specific
              stage tag (e.g. only when tagged "Lost"). Owner must have Gmail connected — if not, a "skipped" entry
              is logged on the lead so you can re-send manually.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">{rules.length} rule{rules.length === 1 ? "" : "s"} configured</p>
          <button
            data-testid="stage-rule-new-btn"
            onClick={openNew}
            disabled={templates.length === 0}
            className="px-3 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium inline-flex items-center gap-1.5"
          >
            <Plus size={14} /> New trigger
          </button>
        </div>

        {templates.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2 text-sm text-amber-800">
            <AlertTriangle size={14} className="mt-0.5" />
            <div>
              No email templates exist yet. Create at least one in{" "}
              <a href="/admin/email-templates" className="underline font-medium">Admin → Email Templates</a> first.
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          {rules.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="stage-rules-empty">
              <Zap size={28} className="mx-auto mb-2 text-slate-300" />
              No triggers configured yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-2.5">When stage →</th>
                  <th className="px-5 py-2.5">Tag</th>
                  <th className="px-5 py-2.5">Send template</th>
                  <th className="px-5 py-2.5">Status</th>
                  <th className="px-5 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} data-testid={`stage-rule-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-medium text-slate-900">{r.to_stage}</td>
                    <td className="px-5 py-3 text-slate-600">{r.tag || <span className="text-slate-400 italic">any</span>}</td>
                    <td className="px-5 py-3 text-slate-700">{r.template_name || r.template_id}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                        r.is_active
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-500 border-slate-200"
                      }`}>{r.is_active ? "Active" : "Paused"}</span>
                    </td>
                    <td className="px-5 py-3 text-right space-x-1">
                      <button onClick={() => openEdit(r)} className="text-xs text-slate-600 hover:text-orange-600 px-2 py-1 rounded hover:bg-slate-100" data-testid={`stage-rule-edit-${r.id}`}>Edit</button>
                      <button onClick={() => remove(r)} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50" data-testid={`stage-rule-delete-${r.id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="stage-rule-editor">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">{editing === "new" ? "New trigger" : "Edit trigger"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">When stage moves to *</label>
                <select data-testid="stage-rule-form-stage" value={form.to_stage} onChange={(e) => setForm({ ...form, to_stage: e.target.value })} className="sr-input">
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Only when tag is</label>
                <select data-testid="stage-rule-form-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className="sr-input">
                  <option value="">— any tag (always fire) —</option>
                  {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Send template *</label>
                <select data-testid="stage-rule-form-template" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} className="sr-input">
                  <option value="">— pick a template —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Status</label>
                <select value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className="sr-input">
                  <option value="1">Active</option>
                  <option value="0">Paused</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Internal note</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sr-input" placeholder="(optional)" />
              </div>
            </div>
            <style>{`.sr-input{width:100%;padding:.5rem .65rem;border:1px solid rgb(226 232 240);border-radius:.375rem;font-size:.85rem;background:white;outline:none}.sr-input:focus{border-color:rgb(249 115 22);box-shadow:0 0 0 2px rgba(249,115,22,.2)}`}</style>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/50">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
              <button type="submit" data-testid="stage-rule-form-submit" className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium inline-flex items-center gap-1.5">
                <Save size={13} /> {editing === "new" ? "Create" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
